import { getDeviations } from './elementShape';
import { findMaterial, REFERENCE_TEMP_C } from '../data/materials';
import { wirksamerCpk } from './cpk';

// Monte-Carlo simulation for tolerance chains.
//
// For each chain element the user picks a probability distribution
// (normal, uniform, triangular) describing how part-to-part variation
// is expected to be distributed within the element's tolerance band.
// The engine draws N independent samples per element, sums them with
// the element's direction (+1/-1), and produces the empirical
// distribution of the closing dimension M_0.
//
// All sampling goes through an injectable RNG so tests (and the
// "Reproduzierbar" Pro feature) can seed it for deterministic
// assertions. Production simply uses Math.random when no seed is set.

export const MC_DISTRIBUTIONS = Object.freeze({
  NORMAL: 'normal',         // mean = nominal + (l+u)/2, stddev = (u-l)/(6*Cpk), Cpk je Maß (Standard 1)
  UNIFORM: 'uniform',       // [nominal+l, nominal+u]
  TRIANGULAR: 'triangular'  // peak at nominal, edges at nominal+l, nominal+u
});

export const DEFAULT_SAMPLES = 10000;
export const DEFAULT_HISTOGRAM_BINS = 50;

// ─── Confidence levels (sigma band) ─────────────────────────────────
//
// The user picks a confidence band:
//   1. Seit C+17 wirkt die Konfidenz NICHT mehr auf σ der Einzelmaße.
//      Der Normal-Sampler nimmt σ = (u−l) / (6·Cpk) mit dem Cpk je Maß
//      (Standard 1,0, also Toleranz = ±3σ).
//   2. The "fits inside spec limits" status check measures the band at
//      ±K_normal standard deviations (via empirical percentiles), so
//      tightening the confidence makes the status verdict stricter.
//
// Three levels are exposed. We label them by their two-tailed coverage
// for a normal distribution. The naming uses "percent" rather than
// raw sigma counts because most quality docs (ISO 9001, AIAG) talk in
// percent first.

export const MC_CONFIDENCE_LEVELS = Object.freeze({
  P99_73: 'p99_73',  // ±3σ — long-standing default
  P99_99: 'p99_99',  // ±4σ — tighter, used in regulated industries
  P99_9999: 'p99_9999'  // ±6σ — Six-Sigma quality target
});

/**
 * Resolved properties for a confidence level. `k` is the sigma
 * multiplier; `lowerP` / `upperP` are the percentile cut-offs used to
 * decide status (mirrors the symmetric ±k·σ band on a normal).
 */
const CONFIDENCE_TABLE = Object.freeze({
  [MC_CONFIDENCE_LEVELS.P99_73]:   { k: 3, lowerP: 0.00135, upperP: 0.99865, label: '99.73 %', sigmaLabel: '±3σ' },
  [MC_CONFIDENCE_LEVELS.P99_99]:   { k: 4, lowerP: 0.0000317, upperP: 0.9999683, label: '99.99 %', sigmaLabel: '±4σ' },
  [MC_CONFIDENCE_LEVELS.P99_9999]: { k: 6, lowerP: 0.000000001, upperP: 0.999999999, label: '99.9999 %', sigmaLabel: '±6σ' }
});

export const DEFAULT_CONFIDENCE = MC_CONFIDENCE_LEVELS.P99_73;

export function getConfidenceConfig(level) {
  return CONFIDENCE_TABLE[level] || CONFIDENCE_TABLE[DEFAULT_CONFIDENCE];
}

// ─── Seeded PRNG (Mulberry32) ───────────────────────────────────────
//
// Used when the user enters a numeric seed for reproducibility. Fast,
// 2^32 period — overkill for tolerance work but easy to reason about
// and stable across browsers/Node.

export function mulberry32(seed) {
  let a = (Number(seed) >>> 0);
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Distribution samplers ──────────────────────────────────────────

function normalSampler(mean, stddev, rng) {
  return () => {
    // Box-Muller. Reject u1=0 to avoid log(0).
    let u1 = rng();
    while (u1 === 0) u1 = rng();
    const u2 = rng();
    return mean + stddev * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };
}

function uniformSampler(min, max, rng) {
  const span = max - min;
  return () => min + rng() * span;
}

function triangularSampler(min, peak, max, rng) {
  const span = max - min;
  // Edge case: degenerate distribution where min == max.
  if (span === 0) return () => min;
  const F = (peak - min) / span;
  return () => {
    const u = rng();
    if (u < F) return min + Math.sqrt(u * span * (peak - min));
    return max - Math.sqrt((1 - u) * span * (max - peak));
  };
}

/**
 * Builds a sampler for one element based on its distribution choice.
 *
 * Normal: σ aus dem Cpk je Maß (C+17), Toleranz = ±3·Cpk·σ, Mitte =
 * Toleranzmitte. Die Konfidenz-Einstellung spielt hier keine Rolle.
 */
export function buildElementSampler(element, rng = Math.random) {
  const nominal = Number(element.nominal) || 0;
  const { lower, upper } = getDeviations(element);
  const dist = element.distribution || MC_DISTRIBUTIONS.NORMAL;

  const min = nominal + lower;
  const max = nominal + upper;

  switch (dist) {
    case MC_DISTRIBUTIONS.UNIFORM:
      return uniformSampler(min, max, rng);
    case MC_DISTRIBUTIONS.TRIANGULAR: {
      // Spitze am Nennmaß, aber nie außerhalb des Feldes: bei gleich
      // signierten Abmaßen (+0,1/+0,3) läge das Nennmaß sonst draußen und
      // der Sampler würfelte Werte außerhalb der Toleranz.
      const peak = Math.min(Math.max(nominal, min), max);
      return triangularSampler(min, peak, max, rng);
    }
    case MC_DISTRIBUTIONS.NORMAL:
    default: {
      const mean = (min + max) / 2;
      const stddev = (max - min) / (6 * wirksamerCpk(element));
      return normalSampler(mean, stddev, rng);
    }
  }
}

// ─── Statistics ─────────────────────────────────────────────────────

function meanOf(samples) {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i];
  return sum / samples.length;
}

function stddevOf(samples, mean) {
  let sumSq = 0;
  for (let i = 0; i < samples.length; i++) {
    const d = samples[i] - mean;
    sumSq += d * d;
  }
  // sample stddev (n-1) — for n in the thousands the difference is negligible
  // but it's the textbook-correct estimator.
  return Math.sqrt(sumSq / (samples.length - 1));
}

/**
 * Linear-interpolated percentile from a sorted array.
 * `p` is in [0, 1]. Uses the type-7 method (numpy default).
 */
export function percentile(sortedSamples, p) {
  const n = sortedSamples.length;
  if (n === 0) return NaN;
  if (n === 1) return sortedSamples[0];
  const pos = (n - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sortedSamples[lo];
  return sortedSamples[lo] + (sortedSamples[hi] - sortedSamples[lo]) * (pos - lo);
}

/**
 * Process capability indices Cp and Cpk against optional spec limits.
 * Returns null when both limits are missing or stddev is non-positive.
 */
export function processCapability(mean, stddev, limits) {
  if (!limits) return null;
  if (!(stddev > 0)) return null;
  const lsl = limits.min;
  const usl = limits.max;

  let cp = null;
  if (lsl !== null && lsl !== undefined && usl !== null && usl !== undefined) {
    cp = (usl - lsl) / (6 * stddev);
  }

  const cpu = (usl !== null && usl !== undefined) ? (usl - mean) / (3 * stddev) : null;
  const cpl = (lsl !== null && lsl !== undefined) ? (mean - lsl) / (3 * stddev) : null;

  let cpk;
  if (cpu !== null && cpl !== null) cpk = Math.min(cpu, cpl);
  else if (cpu !== null) cpk = cpu;
  else if (cpl !== null) cpk = cpl;
  else cpk = null;

  return { cp, cpk, cpu, cpl };
}

// ─── Histogram ──────────────────────────────────────────────────────

/**
 * Builds equal-width bins covering [min(samples), max(samples)].
 * Returns { bins: number[bins+1] (edges), counts: number[bins] }.
 */
export function histogram(samples, binCount = DEFAULT_HISTOGRAM_BINS) {
  if (samples.length === 0) return { bins: [], counts: [] };
  let lo = samples[0], hi = samples[0];
  for (let i = 1; i < samples.length; i++) {
    if (samples[i] < lo) lo = samples[i];
    if (samples[i] > hi) hi = samples[i];
  }
  if (lo === hi) {
    // Degenerate: every sample identical. Make a single bin around the value.
    return { bins: [lo, lo + 1e-9], counts: [samples.length] };
  }
  const width = (hi - lo) / binCount;
  const counts = new Array(binCount).fill(0);
  for (let i = 0; i < samples.length; i++) {
    let idx = Math.floor((samples[i] - lo) / width);
    if (idx === binCount) idx = binCount - 1; // include the right edge
    counts[idx]++;
  }
  const bins = new Array(binCount + 1);
  for (let i = 0; i <= binCount; i++) bins[i] = lo + i * width;
  return { bins, counts };
}

// ─── Engine ─────────────────────────────────────────────────────────

/**
 * Runs the Monte-Carlo simulation.
 *
 * @param {Array} elements - chain elements with optional .distribution
 * @param {object} [options]
 * @param {object} [options.limits]       - { min, max } spec limits for status/Cpk
 * @param {number} [options.samples]      - sample count (default 10_000)
 * @param {number} [options.binCount]     - histogram bins (default 50)
 * @param {function} [options.random]     - injectable RNG, defaults to Math.random
 * @param {number} [options.seed]         - optional seed; if set and random not given,
 *                                          a Mulberry32 PRNG with this seed is used
 * @param {string} [options.confidence]   - MC_CONFIDENCE_LEVELS value;
 *                                          drives the status band (percentiles)
 * @returns {{
 *   nominal: number, mean: number, stddev: number,
 *   min: number, max: number, tolerance: number,
 *   percentiles: { p1, p5, p50, p95, p99 },
 *   bandLow: number, bandHigh: number,
 *   capability: { cp, cpk, cpu, cpl } | null,
 *   histogram: { bins: number[], counts: number[] },
 *   sampleCount: number,
 *   confidence: string,
 *   k: number,
 *   seed: number | null,
 *   status: 'ok' | 'error' | 'none'
 * }}
 */
export function calculateMonteCarlo(elements, options = {}) {
  const {
    limits = { min: null, max: null },
    samples: N = DEFAULT_SAMPLES,
    binCount = DEFAULT_HISTOGRAM_BINS,
    seed = null,
    confidence = DEFAULT_CONFIDENCE
  } = options;

  // RNG resolution priority: explicit `random` > seed > Math.random
  const random = options.random
    ? options.random
    : (seed !== null && seed !== undefined && Number.isFinite(Number(seed)))
        ? mulberry32(Number(seed))
        : Math.random;

  const conf = getConfidenceConfig(confidence);

  if (!elements || elements.length === 0) {
    return {
      nominal: 0, mean: 0, stddev: 0,
      min: 0, max: 0, tolerance: 0,
      percentiles: { p1: 0, p5: 0, p50: 0, p95: 0, p99: 0 },
      bandLow: 0, bandHigh: 0,
      capability: null,
      histogram: { bins: [], counts: [] },
      sampleCount: 0,
      confidence,
      k: conf.k,
      seed: seed ?? null,
      status: 'none'
    };
  }

  // Pre-build samplers + directions so the inner loop is tight.
  const samplers = elements.map(el => buildElementSampler(el, random));
  const directions = elements.map(el => Number(el.direction) === -1 ? -1 : 1);
  // Nominal closing dimension (deterministic, for reference + status).
  // Bei aktivem thermal-Modus bleibt das Nominalmaß auf Tref bezogen —
  // die Zeichnung gibt das Maß bei 20 °C an, alle thermischen Effekte
  // sind reine Auslenkungen darum herum.
  let nominal = 0;
  for (let i = 0; i < elements.length; i++) {
    nominal += directions[i] * (Number(elements[i].nominal) || 0);
  }

  // ─── Thermal-Vorbereitung (Task #27) ─────────────────────────────
  // Pro Sample wird eine Temperatur T uniform aus [tMin, tMax] gezogen
  // — Annahme: die Baugruppe ist überall homogen warm, kein Gradient.
  // Der Beitrag aller Elemente bei T ist linear in T, also können wir
  // den summierten Koeffizienten K_total = Σ dir_i · n_i · α_i einmal
  // vorab berechnen und im Sample-Loop nur eine Multiplikation
  // benötigen. Materialien ohne Eintrag tragen 0 bei → Elemente ohne
  // material erscheinen für den thermischen Anteil neutral.
  const thermal = options.thermal;
  let kTotal = 0;
  let tMin = REFERENCE_TEMP_C;
  let tSpan = 0;
  if (thermal && thermal.enabled
      && Number.isFinite(Number(thermal.tMin))
      && Number.isFinite(Number(thermal.tMax))) {
    tMin = Number(thermal.tMin);
    const tMax = Number(thermal.tMax);
    tSpan = tMax - tMin;
    for (let i = 0; i < elements.length; i++) {
      const mat = findMaterial(elements[i].material);
      if (!mat) continue;
      kTotal += directions[i] * (Number(elements[i].nominal) || 0) * mat.alpha;
    }
  }
  // Effektiv aktiv NUR wenn kein Element thermisch beiträgt würde sonst
  // verschoben sich die seeded random-Sequenzen gegenüber dem Baseline,
  // obwohl der Mean-Shift bit-identisch 0 wäre — auch Cosmetic für
  // reproducibility wichtig (z.B. Vergleich „mit/ohne thermal" mit
  // gleichem Seed).
  const thermalActive = kTotal !== 0;

  // Draw N closing-dimension samples.
  const samples = new Float64Array(N);
  for (let s = 0; s < N; s++) {
    let acc = 0;
    for (let i = 0; i < samplers.length; i++) {
      acc += directions[i] * samplers[i]();
    }
    if (thermalActive) {
      // T uniform in [tMin, tMax], thermische Gesamtverschiebung
      // entsprechend dem vorab summierten Koeffizienten.
      const T = tMin + tSpan * random();
      acc += kTotal * (T - REFERENCE_TEMP_C);
    }
    samples[s] = acc;
  }

  // Sort once for percentiles.
  const sorted = Float64Array.from(samples).sort();

  const mean = meanOf(samples);
  const stddev = stddevOf(samples, mean);
  const minV = sorted[0];
  const maxV = sorted[sorted.length - 1];
  const percentiles = {
    p1: percentile(sorted, 0.01),
    p5: percentile(sorted, 0.05),
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    p99: percentile(sorted, 0.99)
  };
  const capability = processCapability(mean, stddev, limits);

  // Status band is the symmetric percentile interval matching the
  // selected confidence level. Using full-range minV/maxV would be too
  // pessimistic — the tail keeps growing with N. p1/p99 would be too
  // tight for ±3σ. So the band is parameterized by the confidence pick.
  const bandLow = percentile(sorted, conf.lowerP);
  const bandHigh = percentile(sorted, conf.upperP);

  let status = 'none';
  if (limits && (limits.min !== null || limits.max !== null)) {
    const lo = limits.min ?? -Infinity;
    const hi = limits.max ?? Infinity;
    status = (bandLow >= lo && bandHigh <= hi) ? 'ok' : 'error';
  }

  return {
    nominal,
    mean,
    stddev,
    min: minV,
    max: maxV,
    tolerance: maxV - minV,
    percentiles,
    bandLow,
    bandHigh,
    capability,
    histogram: histogram(samples, binCount),
    sampleCount: N,
    confidence,
    k: conf.k,
    seed: seed ?? null,
    status
  };
}
